/**
 * Cosmetics System
 * Defines all available titles, calling cards, emblems, and avatars
 */

// ============================================================================
// TITLES
// ============================================================================

export interface TitleDefinition {
  id: string;
  name: string;
  description: string;
  category: 'default' | 'rank' | 'archetype' | 'achievement' | 'operation' | 'milestone' | 'crossapp' | 'promo';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  secret?: boolean; // Hidden until unlocked
  unlockRequirement?: {
    type: 'intel' | 'streak' | 'sessions' | 'archetype' | 'pillar' | 'crossapp';
    value: number;
    pillar?: 'strength' | 'endurance' | 'bodyweight' | 'recovery' | 'consistency';
    description: string;
    crossappType?: 'link' | 'dual-weeks' | 'shooter-grade' | 'combined-score' | 'both-elite';
  };
}

export const TITLES: Record<string, TitleDefinition> = {
  // Promo — exclusive, granted imperatively by a redeem code (e.g. GOOSETROOP).
  // No unlockRequirement: it's granted via progressionService.unlockCosmetic().
  'Goose Troop': {
    id: 'Goose Troop',
    name: 'Goose Troop',
    description: 'You were here first. Shirt buyers only.',
    category: 'promo',
    rarity: 'epic',
  },
  // Default
  'Recruit': {
    id: 'Recruit',
    name: 'Recruit',
    description: 'Starting title for all operators',
    category: 'default',
    rarity: 'common',
  },

  // Enlisted Rank titles (E1-E7) - Unlock at same Intel thresholds as avatars
  'Private': {
    id: 'Private',
    name: 'PVT',
    description: 'Achieved E-1 rank',
    category: 'rank',
    rarity: 'common',
    unlockRequirement: { type: 'intel', value: 500, description: 'Earn 500 lifetime Intel' },
  },
  'PFC': {
    id: 'PFC',
    name: 'PFC',
    description: 'Achieved E-2 rank - Private First Class',
    category: 'rank',
    rarity: 'common',
    unlockRequirement: { type: 'intel', value: 1500, description: 'Earn 1,500 lifetime Intel' },
  },
  'Specialist': {
    id: 'Specialist',
    name: 'SPC',
    description: 'Achieved E-4 rank - Specialist',
    category: 'rank',
    rarity: 'uncommon',
    unlockRequirement: { type: 'intel', value: 3000, description: 'Earn 3,000 lifetime Intel' },
  },
  'Corporal': {
    id: 'Corporal',
    name: 'CPL',
    description: 'Achieved E-4 rank - Corporal',
    category: 'rank',
    rarity: 'uncommon',
    unlockRequirement: { type: 'intel', value: 5000, description: 'Earn 5,000 lifetime Intel' },
  },
  'Sergeant': {
    id: 'Sergeant',
    name: 'SGT',
    description: 'Achieved E-5 rank - Sergeant',
    category: 'rank',
    rarity: 'rare',
    unlockRequirement: { type: 'intel', value: 8000, description: 'Earn 8,000 lifetime Intel' },
  },
  'Staff Sergeant': {
    id: 'Staff Sergeant',
    name: 'SSG',
    description: 'Achieved E-6 rank - Staff Sergeant',
    category: 'rank',
    rarity: 'rare',
    unlockRequirement: { type: 'intel', value: 12000, description: 'Earn 12,000 lifetime Intel' },
  },
  'SFC': {
    id: 'SFC',
    name: 'SFC',
    description: 'Achieved E-7 rank - Sergeant First Class',
    category: 'rank',
    rarity: 'epic',
    unlockRequirement: { type: 'intel', value: 18000, description: 'Earn 18,000 lifetime Intel' },
  },

  // Special Operations titles - Elite achievements
  'Ranger': {
    id: 'Ranger',
    name: 'Ranger',
    description: '75th Ranger Regiment - Lead the Way',
    category: 'rank',
    rarity: 'epic',
    unlockRequirement: { type: 'intel', value: 25000, description: 'Earn 25,000 lifetime Intel' },
  },
  'Special Forces': {
    id: 'Special Forces',
    name: 'Green Beret',
    description: 'Special Forces Operator - De Oppresso Liber',
    category: 'rank',
    rarity: 'epic',
    unlockRequirement: { type: 'intel', value: 40000, description: 'Earn 40,000 lifetime Intel' },
  },
  'DEVGRU': {
    id: 'DEVGRU',
    name: 'DEVGRU',
    description: 'Naval Special Warfare Development Group',
    category: 'rank',
    rarity: 'legendary',
    unlockRequirement: { type: 'intel', value: 60000, description: 'Earn 60,000 lifetime Intel' },
  },
  'Tier One': {
    id: 'Tier One',
    name: 'Tier One',
    description: 'Delta Force / CAG - The Unit',
    category: 'rank',
    rarity: 'legendary',
    unlockRequirement: { type: 'intel', value: 100000, description: 'Earn 100,000 lifetime Intel' },
  },
  'Ground Branch': {
    id: 'Ground Branch',
    name: 'Ground Branch',
    description: 'CIA Special Activities Center - The quiet professionals',
    category: 'rank',
    rarity: 'legendary',
    secret: true, // Hidden until unlocked
    unlockRequirement: { type: 'intel', value: 200000, description: 'Earn 200,000 lifetime Intel' },
  },

  // Archetype titles
  'The Logistics Specialist': {
    id: 'The Logistics Specialist',
    name: 'The Logistics Specialist',
    description: 'Unlocked Pack Mule archetype',
    category: 'archetype',
    rarity: 'epic',
  },
  'The Door Kicker': {
    id: 'The Door Kicker',
    name: 'The Door Kicker',
    description: 'Unlocked Breacher archetype',
    category: 'archetype',
    rarity: 'epic',
  },
  'The Pathfinder': {
    id: 'The Pathfinder',
    name: 'The Pathfinder',
    description: 'Unlocked Scout archetype',
    category: 'archetype',
    rarity: 'epic',
  },
  'The Climber': {
    id: 'The Climber',
    name: 'The Climber',
    description: 'Unlocked Assaulter archetype',
    category: 'archetype',
    rarity: 'epic',
  },
  'The All-Rounder': {
    id: 'The All-Rounder',
    name: 'The All-Rounder',
    description: 'Unlocked Operator archetype',
    category: 'archetype',
    rarity: 'legendary',
  },

  // Archetype Tier Titles — Pack Mule
  'pack-mule-initiate': {
    id: 'pack-mule-initiate',
    name: 'Load Bearer',
    description: 'Pack Mule Initiate — 25+ ruck miles',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'pack-mule-qualified': {
    id: 'pack-mule-qualified',
    name: 'Pack Mule',
    description: 'Pack Mule Qualified — 50+ ruck miles',
    category: 'archetype',
    rarity: 'rare',
  },
  'pack-mule-specialist': {
    id: 'pack-mule-specialist',
    name: 'Iron Mule',
    description: 'Pack Mule Specialist — 100+ ruck miles',
    category: 'archetype',
    rarity: 'epic',
  },
  'pack-mule-master': {
    id: 'pack-mule-master',
    name: 'Atlas Carrier',
    description: 'Pack Mule Master — 200+ ruck miles',
    category: 'archetype',
    rarity: 'legendary',
  },

  // Archetype Tier Titles — Breacher
  'breacher-initiate': {
    id: 'breacher-initiate',
    name: 'Ram',
    description: 'Breacher Initiate — 100K+ lbs volume',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'breacher-qualified': {
    id: 'breacher-qualified',
    name: 'Entry Man',
    description: 'Breacher Qualified — 300K+ lbs volume',
    category: 'archetype',
    rarity: 'rare',
  },
  'breacher-specialist': {
    id: 'breacher-specialist',
    name: 'Shock Breacher',
    description: 'Breacher Specialist — 500K+ lbs volume',
    category: 'archetype',
    rarity: 'epic',
  },
  'breacher-master': {
    id: 'breacher-master',
    name: 'Door Killer',
    description: 'Breacher Master — 750K+ lbs volume',
    category: 'archetype',
    rarity: 'legendary',
  },

  // Archetype Tier Titles — Scout
  'scout-initiate': {
    id: 'scout-initiate',
    name: 'Pathfinder',
    description: 'Scout Initiate — 50+ run miles',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'scout-qualified': {
    id: 'scout-qualified',
    name: 'Recon Specialist',
    description: 'Scout Qualified — 100+ run miles',
    category: 'archetype',
    rarity: 'rare',
  },
  'scout-specialist': {
    id: 'scout-specialist',
    name: 'Long Walker',
    description: 'Scout Specialist — 200+ run miles',
    category: 'archetype',
    rarity: 'epic',
  },
  'scout-master': {
    id: 'scout-master',
    name: 'Ghost Scout',
    description: 'Scout Master — 300+ run miles',
    category: 'archetype',
    rarity: 'legendary',
  },

  // Archetype Tier Titles — Assaulter
  'assaulter-initiate': {
    id: 'assaulter-initiate',
    name: 'Climber',
    description: 'Assaulter Initiate — 150+ pull-ups',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'assaulter-qualified': {
    id: 'assaulter-qualified',
    name: 'Assaulter',
    description: 'Assaulter Qualified — 300+ pull-ups',
    category: 'archetype',
    rarity: 'rare',
  },
  'assaulter-specialist': {
    id: 'assaulter-specialist',
    name: 'CQB Specialist',
    description: 'Assaulter Specialist — 500+ pull-ups',
    category: 'archetype',
    rarity: 'epic',
  },
  'assaulter-master': {
    id: 'assaulter-master',
    name: 'Urban Hunter',
    description: 'Assaulter Master — 750+ pull-ups',
    category: 'archetype',
    rarity: 'legendary',
  },

  // Archetype Tier Titles — Operator
  'operator-initiate': {
    id: 'operator-initiate',
    name: 'Operator',
    description: 'Operator Initiate — 40+ sessions',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'operator-qualified': {
    id: 'operator-qualified',
    name: 'Tier One',
    description: 'Operator Qualified — 80+ sessions',
    category: 'archetype',
    rarity: 'rare',
  },
  'operator-specialist': {
    id: 'operator-specialist',
    name: 'Unit Leader',
    description: 'Operator Specialist — 150+ sessions',
    category: 'archetype',
    rarity: 'epic',
  },
  'operator-master': {
    id: 'operator-master',
    name: 'Mission Commander',
    description: 'Operator Master — 250+ sessions',
    category: 'archetype',
    rarity: 'legendary',
  },

  // Achievement titles
  'Iron Will': {
    id: 'Iron Will',
    name: 'Iron Will',
    description: 'Maintained a 30-day training streak',
    category: 'achievement',
    rarity: 'rare',
  },
  'Relentless': {
    id: 'Relentless',
    name: 'Relentless',
    description: 'Maintained a 60-day training streak',
    category: 'achievement',
    rarity: 'epic',
  },
  'Unstoppable': {
    id: 'Unstoppable',
    name: 'Unstoppable',
    description: 'Maintained a 100-day training streak',
    category: 'achievement',
    rarity: 'legendary',
  },
  'Century Club': {
    id: 'Century Club',
    name: 'Century Club',
    description: 'Completed 100 training sessions',
    category: 'milestone',
    rarity: 'rare',
  },
  'Iron Forged': {
    id: 'Iron Forged',
    name: 'Iron Forged',
    description: 'Lifted 1,000,000 lbs total volume',
    category: 'milestone',
    rarity: 'epic',
  },

  // Operation titles
  'Winter Warrior': {
    id: 'Winter Warrior',
    name: 'Winter Warrior',
    description: 'Completed Operation Winter Forge',
    category: 'operation',
    rarity: 'rare',
  },
  'Arctic Operator': {
    id: 'Arctic Operator',
    name: 'Arctic Operator',
    description: 'Completed all Winter Forge objectives with 100% efficiency',
    category: 'operation',
    rarity: 'epic',
  },

  // Operation Spearhead titles
  'Erebus Hunter': {
    id: 'Erebus Hunter',
    name: 'Erebus Hunter',
    description: 'Completed Operation Spearhead — all four sectors cleared, EREBUS network dismantled',
    category: 'operation',
    rarity: 'epic',
  },

  // Cross-App (GunfighterOS Integration)
  'LINKED': {
    id: 'LINKED',
    name: 'LINKED',
    description: 'Connected OPMODE and GunfighterOS',
    category: 'crossapp',
    rarity: 'rare',
    unlockRequirement: {
      type: 'crossapp',
      value: 1,
      description: 'Link your GunfighterOS account',
      crossappType: 'link',
    },
  },
  'TACTICAL ATHLETE': {
    id: 'TACTICAL ATHLETE',
    name: 'TACTICAL ATHLETE',
    description: 'Train in both apps for 4 consecutive weeks',
    category: 'crossapp',
    rarity: 'epic',
    unlockRequirement: {
      type: 'crossapp',
      value: 4,
      description: '4 consecutive weeks of dual training',
      crossappType: 'dual-weeks',
    },
  },
  'FULL SPECTRUM': {
    id: 'FULL SPECTRUM',
    name: 'FULL SPECTRUM',
    description: 'Achieved 70+ in both Operator Score and Shooter Grade',
    category: 'crossapp',
    rarity: 'epic',
    unlockRequirement: {
      type: 'crossapp',
      value: 70,
      description: '70+ in both apps',
      crossappType: 'both-elite',
    },
  },
  'COMBAT READY': {
    id: 'COMBAT READY',
    name: 'COMBAT READY',
    description: 'Combined Operational Readiness of 160+',
    category: 'crossapp',
    rarity: 'legendary',
    unlockRequirement: {
      type: 'crossapp',
      value: 160,
      description: 'Combined score of 160+',
      crossappType: 'combined-score',
    },
  },
};

// ============================================================================
// CALLING CARDS
// ============================================================================

export interface CallingCardDefinition {
  id: string;
  name: string;
  description: string;
  image: string; // Background image path
  category: 'default' | 'archetype' | 'achievement' | 'operation' | 'seasonal' | 'crossapp' | 'promo';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  unlockRequirement?: {
    type: 'crossapp';
    value: number;
    description: string;
    crossappType?: 'link' | 'dual-weeks' | 'shooter-grade' | 'combined-score' | 'both-elite';
  };
}

export const CALLING_CARDS: Record<string, CallingCardDefinition> = {
  // Promo — exclusive, granted by redeem code (e.g. GOOSETROOP).
  // TODO: add art at public/calling-cards/goose-troop.png before shipping.
  'goose-troop': {
    id: 'goose-troop',
    name: 'Goose Troop',
    description: 'Issued to Goose Troop — shirt buyers only.',
    // TODO(art): goose-troop artwork not exported yet. See the note on the
    // other placeholder cards — a missing file renders a permanently blank
    // tile. Now reachable because the 'promo' category finally renders.
    image: '/calling-cards/default.png',
    category: 'promo',
    rarity: 'epic',
  },
  'default': {
    id: 'default',
    name: 'Standard Issue',
    description: 'Default operator calling card',
    image: '/calling-cards/default.png',
    category: 'default',
    rarity: 'common',
  },
  'tactical-black': {
    id: 'tactical-black',
    name: 'Tactical Black',
    description: 'Clean, professional operator aesthetic',
    image: '/calling-cards/tactical-black.png',
    category: 'default',
    rarity: 'common',
  },
  'pack-mule': {
    id: 'pack-mule',
    name: 'Pack Mule',
    description: 'Reach Pack Mule Qualified tier',
    image: '/archetypes/packmule.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'breacher': {
    id: 'breacher',
    name: 'Breacher',
    description: 'Reach Breacher Qualified tier',
    image: '/archetypes/breacher.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'scout': {
    id: 'scout',
    name: 'Scout',
    description: 'Reach Scout Qualified tier',
    image: '/archetypes/scout.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'assaulter': {
    id: 'assaulter',
    name: 'Assaulter',
    description: 'Reach Assaulter Qualified tier',
    image: '/archetypes/assaulter.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'operator': {
    id: 'operator',
    name: 'Operator',
    description: 'Reach Operator Qualified tier',
    image: '/archetypes/operator.png',
    category: 'archetype',
    rarity: 'legendary',
  },
  'winter-forge': {
    id: 'winter-forge',
    name: 'Winter Forge',
    description: 'Completed Operation Winter Forge',
    image: '/calling-cards/winter-forge.png',
    category: 'operation',
    rarity: 'rare',
  },
  'streak-30': {
    id: 'streak-30',
    name: '30-Day Streak',
    description: 'Maintained a 30-day training streak',
    // TODO(art): no PNG exists for this card yet. Pointing at default.png
    // rather than a missing file — a referenced-but-absent image renders as a
    // permanently blank tile plus a console 404 for every user, and next/image
    // sets a srcset that defeats an onError src swap. Swap this back the moment
    // the real artwork lands in public/calling-cards/.
    image: '/calling-cards/default.png',
    category: 'achievement',
    rarity: 'rare',
  },

  // Operation Spearhead calling card
  'territorial-dominance': {
    id: 'territorial-dominance',
    name: 'Territorial Dominance',
    description: 'Completed Operation Spearhead — all four sectors secured against EREBUS',
    // TODO(art): no PNG exists for this card yet. Pointing at default.png
    // rather than a missing file — a referenced-but-absent image renders as a
    // permanently blank tile plus a console 404 for every user, and next/image
    // sets a srcset that defeats an onError src swap. Swap this back the moment
    // the real artwork lands in public/calling-cards/.
    image: '/calling-cards/default.png',
    category: 'operation',
    rarity: 'epic',
  },

  // Cross-App (GunfighterOS Integration)
  'gunfighter-access': {
    id: 'gunfighter-access',
    name: 'GunfighterOS Access',
    description: 'Linked OPMODE and GunfighterOS accounts',
    image: '/calling-cards/gunfighter-access.png',
    category: 'crossapp',
    rarity: 'epic',
    unlockRequirement: {
      type: 'crossapp',
      value: 1,
      description: 'Link your GunfighterOS account',
      crossappType: 'link',
    },
  },
};

// ============================================================================
// EMBLEMS
// ============================================================================

export interface EmblemDefinition {
  id: string;
  name: string;
  description: string;
  icon: string; // Emoji or icon path
  category: 'default' | 'archetype' | 'achievement' | 'operation' | 'pillar' | 'crossapp';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  secret?: boolean; // Hidden until unlocked
  unlockRequirement?: {
    type: 'crossapp';
    value: number;
    description: string;
    crossappType?: 'link' | 'dual-weeks' | 'shooter-grade' | 'combined-score' | 'both-elite';
  };
}

export const EMBLEMS: Record<string, EmblemDefinition> = {
  'default': {
    id: 'default',
    name: 'OPMODE',
    description: 'Default operator emblem',
    icon: '🎖️',
    category: 'default',
    rarity: 'common',
  },
  'strength-pillar': {
    id: 'strength-pillar',
    name: 'Strength Pillar',
    description: 'Earned 500 Strength Intel',
    icon: '💪',
    category: 'pillar',
    rarity: 'uncommon',
  },
  'endurance-pillar': {
    id: 'endurance-pillar',
    name: 'Endurance Pillar',
    description: 'Earned 500 Endurance Intel',
    icon: '🏃',
    category: 'pillar',
    rarity: 'uncommon',
  },
  'bodyweight-pillar': {
    id: 'bodyweight-pillar',
    name: 'Bodyweight Pillar',
    description: 'Earned 500 Bodyweight Intel',
    icon: '🤸',
    category: 'pillar',
    rarity: 'uncommon',
  },
  'recovery-pillar': {
    id: 'recovery-pillar',
    name: 'Recovery Pillar',
    description: 'Earned 500 Recovery Intel',
    icon: '🌙',
    category: 'pillar',
    rarity: 'uncommon',
  },
  'consistency-pillar': {
    id: 'consistency-pillar',
    name: 'Consistency Pillar',
    description: 'Earned 500 Consistency Intel',
    icon: '🔥',
    category: 'pillar',
    rarity: 'uncommon',
  },
  'pack-mule': {
    id: 'pack-mule',
    name: 'Pack Mule',
    description: 'Reach Pack Mule Initiate tier',
    icon: '/emblems/packmule.png',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'breacher': {
    id: 'breacher',
    name: 'Breacher',
    description: 'Reach Breacher Initiate tier',
    icon: '/emblems/breacher.png',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'scout': {
    id: 'scout',
    name: 'Scout',
    description: 'Reach Scout Initiate tier',
    icon: '🧭',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'assaulter': {
    id: 'assaulter',
    name: 'Assaulter',
    description: 'Reach Assaulter Initiate tier',
    icon: '/emblems/assaulter.png',
    category: 'archetype',
    rarity: 'uncommon',
  },
  'operator': {
    id: 'operator',
    name: 'Operator',
    description: 'Reach Operator Initiate tier',
    icon: '/emblems/operator.png',
    category: 'archetype',
    rarity: 'rare',
  },
  'winter-forge': {
    id: 'winter-forge',
    name: 'Winter Forge',
    description: 'Completed Operation Winter Forge',
    icon: '/emblems/winter-forge.png',
    category: 'operation',
    rarity: 'rare',
  },

  // Operation Spearhead emblems
  'spearhead-emblem': {
    id: 'spearhead-emblem',
    name: 'Spearhead',
    description: 'Completed Operation Spearhead — all four sectors secured against EREBUS',
    icon: '/emblems/spearhead.png',
    category: 'operation',
    rarity: 'epic',
  },
  'force-multiplier': {
    id: 'force-multiplier',
    name: 'Force Multiplier',
    description: 'Achieved 90% control in any sector during Operation Spearhead',
    icon: '/emblems/force-multiplier.png',
    category: 'operation',
    rarity: 'rare',
  },
  'consistency-protocol': {
    id: 'consistency-protocol',
    name: 'Consistency Protocol',
    description: 'Maintained 0.8+ consistency index for 14 consecutive days during Operation Spearhead',
    icon: '/emblems/consistency-protocol.png',
    category: 'operation',
    rarity: 'rare',
  },

  'first-blood': {
    id: 'first-blood',
    name: 'First Blood',
    description: 'Completed your first session',
    icon: '🩸',
    category: 'achievement',
    rarity: 'common',
  },
  'century': {
    id: 'century',
    name: 'Century',
    description: 'Completed 100 sessions',
    icon: '💯',
    category: 'achievement',
    rarity: 'rare',
  },

  // Cross-App (GunfighterOS Integration)
  'crossapp-crosshairs': {
    id: 'crossapp-crosshairs',
    name: 'Crosshairs',
    description: 'GunfighterOS account linked',
    icon: '/emblems/crosshairs.png',
    category: 'crossapp',
    rarity: 'rare',
    unlockRequirement: {
      type: 'crossapp',
      value: 1,
      description: 'Link your GunfighterOS account',
      crossappType: 'link',
    },
  },
  'crossapp-dual-threat': {
    id: 'crossapp-dual-threat',
    name: 'Dual Threat',
    description: '8 weeks of dual-app training',
    icon: '/emblems/dual-threat.png',
    category: 'crossapp',
    rarity: 'epic',
    unlockRequirement: {
      type: 'crossapp',
      value: 8,
      description: '8 consecutive weeks of dual training',
      crossappType: 'dual-weeks',
    },
  },
  'crossapp-apex-predator': {
    id: 'crossapp-apex-predator',
    name: 'Apex Predator',
    description: 'Maximum operational readiness achieved',
    icon: '🦅',
    category: 'crossapp',
    rarity: 'legendary',
    unlockRequirement: {
      type: 'crossapp',
      value: 180,
      description: 'Combined score of 180+',
      crossappType: 'combined-score',
    },
  },
};

// ============================================================================
// AVATARS
// ============================================================================

export interface AvatarDefinition {
  id: string;
  name: string;
  description: string;
  image: string;
  category: 'default' | 'archetype' | 'achievement' | 'operation' | 'rank' | 'crossapp';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  secret?: boolean; // Hidden until unlocked
  unlockRequirement?: {
    type: 'intel' | 'streak' | 'sessions' | 'archetype' | 'crossapp';
    value: number;
    description: string;
    crossappType?: 'link' | 'dual-weeks' | 'shooter-grade' | 'combined-score' | 'both-elite';
  };
}

export const AVATARS: Record<string, AvatarDefinition> = {
  // ============================================================================
  // ENLISTED RANKS (E1-E7) - Unlock via lifetime Intel
  // ============================================================================
  'default': {
    id: 'default',
    name: 'Recruit',
    description: 'Starting operator avatar - every journey begins here',
    image: '/avatars/recruit.png',
    category: 'default',
    rarity: 'common',
  },
  'rank-pvt': {
    id: 'rank-pvt',
    name: 'Private (PVT)',
    description: 'E-1 - Foundation established, showing commitment',
    image: '/avatars/private.png',
    category: 'rank',
    rarity: 'common',
    unlockRequirement: {
      type: 'intel',
      value: 500,
      description: 'Earn 500 lifetime Intel',
    },
  },
  'rank-pfc': {
    id: 'rank-pfc',
    name: 'Private First Class (PFC)',
    description: 'E-2 - Building consistency in training',
    image: '/avatars/pfc.png',
    category: 'rank',
    rarity: 'common',
    unlockRequirement: {
      type: 'intel',
      value: 1500,
      description: 'Earn 1,500 lifetime Intel',
    },
  },
  'rank-spc': {
    id: 'rank-spc',
    name: 'Specialist (SPC)',
    description: 'E-4 - Developing specialized fitness skills',
    image: '/avatars/specialist.png',
    category: 'rank',
    rarity: 'uncommon',
    unlockRequirement: {
      type: 'intel',
      value: 3000,
      description: 'Earn 3,000 lifetime Intel',
    },
  },
  'rank-cpl': {
    id: 'rank-cpl',
    name: 'Corporal (CPL)',
    description: 'E-4 - Junior NCO, leading by example',
    image: '/avatars/corporal.png',
    category: 'rank',
    rarity: 'uncommon',
    unlockRequirement: {
      type: 'intel',
      value: 5000,
      description: 'Earn 5,000 lifetime Intel',
    },
  },
  'rank-sgt': {
    id: 'rank-sgt',
    name: 'Sergeant (SGT)',
    description: 'E-5 - NCO backbone, consistent performer',
    image: '/avatars/sergeant.png',
    category: 'rank',
    rarity: 'rare',
    unlockRequirement: {
      type: 'intel',
      value: 8000,
      description: 'Earn 8,000 lifetime Intel',
    },
  },
  'rank-ssgt': {
    id: 'rank-ssgt',
    name: 'Staff Sergeant (SSG)',
    description: 'E-6 - Senior NCO, tactical expertise proven',
    image: '/avatars/ssgt.png',
    category: 'rank',
    rarity: 'rare',
    unlockRequirement: {
      type: 'intel',
      value: 12000,
      description: 'Earn 12,000 lifetime Intel',
    },
  },
  'rank-sfc': {
    id: 'rank-sfc',
    name: 'Sergeant First Class (SFC)',
    description: 'E-7 - Senior NCO, ready for selection',
    image: '/avatars/sfc.png',
    category: 'rank',
    rarity: 'epic',
    unlockRequirement: {
      type: 'intel',
      value: 18000,
      description: 'Earn 18,000 lifetime Intel',
    },
  },

  // ============================================================================
  // SPECIAL OPERATIONS RANKS - Elite achievements
  // ============================================================================
  'rank-ranger': {
    id: 'rank-ranger',
    name: 'Ranger',
    description: '75th Ranger Regiment - Lead the way',
    image: '/avatars/ranger.png',
    category: 'rank',
    rarity: 'epic',
    unlockRequirement: {
      type: 'intel',
      value: 25000,
      description: 'Earn 25,000 lifetime Intel',
    },
  },
  'rank-sf': {
    id: 'rank-sf',
    name: 'Special Forces',
    description: 'Green Beret - De Oppresso Liber',
    image: '/avatars/special-forces.png',
    category: 'rank',
    rarity: 'epic',
    unlockRequirement: {
      type: 'intel',
      value: 40000,
      description: 'Earn 40,000 lifetime Intel',
    },
  },
  'rank-devgru': {
    id: 'rank-devgru',
    name: 'DEVGRU',
    description: 'Naval Special Warfare Development Group',
    image: '/avatars/devgru.png',
    category: 'rank',
    rarity: 'legendary',
    unlockRequirement: {
      type: 'intel',
      value: 60000,
      description: 'Earn 60,000 lifetime Intel',
    },
  },
  'rank-tier1': {
    id: 'rank-tier1',
    name: 'Tier One Operator',
    description: 'Delta Force / CAG - The Unit',
    image: '/avatars/tier-one.png',
    category: 'rank',
    rarity: 'legendary',
    unlockRequirement: {
      type: 'intel',
      value: 100000,
      description: 'Earn 100,000 lifetime Intel',
    },
  },
  'rank-gb': {
    id: 'rank-gb',
    name: 'Ground Branch Operator',
    description: 'CIA Special Activities Center - The quiet professionals',
    image: '/avatars/ground-branch.png',
    category: 'rank',
    rarity: 'legendary',
    secret: true, // Hidden until unlocked
    unlockRequirement: {
      type: 'intel',
      value: 200000,
      description: 'Earn 200,000 lifetime Intel',
    },
  },
  'pack-mule': {
    id: 'pack-mule',
    name: 'Pack Mule Operator',
    description: 'Reach Pack Mule Specialist tier',
    image: '/archetypes/packmule.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'breacher': {
    id: 'breacher',
    name: 'Breacher Operator',
    description: 'Reach Breacher Specialist tier',
    image: '/archetypes/breacher.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'scout': {
    id: 'scout',
    name: 'Scout Operator',
    description: 'Reach Scout Specialist tier',
    image: '/archetypes/scout.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'assaulter': {
    id: 'assaulter',
    name: 'Assaulter Operator',
    description: 'Reach Assaulter Specialist tier',
    image: '/archetypes/assaulter.png',
    category: 'archetype',
    rarity: 'epic',
  },
  'operator': {
    id: 'operator',
    name: 'Elite Operator',
    description: 'Reach Operator Specialist tier',
    image: '/archetypes/operator.png',
    category: 'archetype',
    rarity: 'legendary',
  },
  'winter-warrior': {
    id: 'winter-warrior',
    name: 'Winter Warrior',
    description: 'Completed Operation Winter Forge',
    image: '/avatars/winter-warrior.png',
    category: 'operation',
    rarity: 'rare',
  },

  // ============================================================================
  // CROSS-APP (GunfighterOS Integration)
  // ============================================================================
  'crossapp-dual-operator': {
    id: 'crossapp-dual-operator',
    name: 'Dual Operator',
    description: 'Linked OPMODE and GunfighterOS accounts',
    image: '/avatars/dual-operator.png',
    category: 'crossapp',
    rarity: 'rare',
    unlockRequirement: {
      type: 'crossapp',
      value: 1,
      description: 'Link your GunfighterOS account',
      crossappType: 'link',
    },
  },
  'crossapp-gunfighter-elite': {
    id: 'crossapp-gunfighter-elite',
    name: 'Gunfighter Elite',
    description: 'Achieved Master shooter grade in GunfighterOS',
    image: '/avatars/gunfighter-elite.png',
    category: 'crossapp',
    rarity: 'epic',
    unlockRequirement: {
      type: 'crossapp',
      value: 90,
      description: 'Reach Master grade (90+) in GunfighterOS',
      crossappType: 'shooter-grade',
    },
  },
  'crossapp-complete-warrior': {
    id: 'crossapp-complete-warrior',
    name: 'Complete Warrior',
    description: 'Elite in both physical and tactical domains',
    image: '/avatars/complete-warrior.png',
    category: 'crossapp',
    rarity: 'legendary',
    unlockRequirement: {
      type: 'crossapp',
      value: 80,
      description: '80+ Operator Score AND 80+ Shooter Grade',
      crossappType: 'both-elite',
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getRarityColor(rarity: string): string {
  switch (rarity) {
    case 'common': return 'text-gray-400 border-gray-500';
    case 'uncommon': return 'text-green-400 border-green-500';
    case 'rare': return 'text-blue-400 border-blue-500';
    case 'epic': return 'text-purple-400 border-purple-500';
    case 'legendary': return 'text-operator-gold border-operator-gold';
    default: return 'text-gray-400 border-gray-500';
  }
}

export function getRarityBgColor(rarity: string): string {
  switch (rarity) {
    case 'common': return 'bg-gray-500/10';
    case 'uncommon': return 'bg-green-500/10';
    case 'rare': return 'bg-blue-500/10';
    case 'epic': return 'bg-purple-500/10';
    case 'legendary': return 'bg-operator-gold/10';
    default: return 'bg-gray-500/10';
  }
}

export function getRarityLabel(rarity: string): string {
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

/**
 * Get all rank-based avatars that should be unlocked based on lifetime Intel
 * Returns avatar IDs that the user has earned
 */
export function getUnlockedRankAvatars(lifetimeIntel: number): string[] {
  const unlocked: string[] = ['default']; // Recruit is always unlocked

  for (const [id, avatar] of Object.entries(AVATARS)) {
    if (
      avatar.category === 'rank' &&
      avatar.unlockRequirement?.type === 'intel' &&
      lifetimeIntel >= avatar.unlockRequirement.value
    ) {
      unlocked.push(id);
    }
  }

  return unlocked;
}

/**
 * Check if a specific avatar should be unlocked based on current stats
 */
export function checkAvatarUnlock(
  avatarId: string,
  stats: {
    lifetimeIntel?: number;
    streak?: number;
    sessions?: number;
    archetypeUnlocked?: string[];
  }
): boolean {
  const avatar = AVATARS[avatarId];
  if (!avatar) return false;

  // Default avatars are always unlocked
  if (avatar.category === 'default') return true;

  const req = avatar.unlockRequirement;
  if (!req) return false;

  switch (req.type) {
    case 'intel':
      return (stats.lifetimeIntel ?? 0) >= req.value;
    case 'streak':
      return (stats.streak ?? 0) >= req.value;
    case 'sessions':
      return (stats.sessions ?? 0) >= req.value;
    case 'archetype':
      // For archetype unlocks, check if the archetype is in the unlocked list
      return stats.archetypeUnlocked?.includes(avatarId) ?? false;
    default:
      return false;
  }
}

/**
 * Get all rank-based titles that should be unlocked based on lifetime Intel
 * Returns title IDs that the user has earned
 */
export function getUnlockedRankTitles(lifetimeIntel: number): string[] {
  const unlocked: string[] = ['Recruit']; // Recruit is always unlocked

  for (const [id, title] of Object.entries(TITLES)) {
    if (
      title.category === 'rank' &&
      title.unlockRequirement?.type === 'intel' &&
      lifetimeIntel >= title.unlockRequirement.value
    ) {
      unlocked.push(id);
    }
  }

  return unlocked;
}

/**
 * Get pillar emblems that should be unlocked based on pillar Intel
 */
export function getUnlockedPillarEmblems(pillarIntel: {
  strength: number;
  endurance: number;
  bodyweight: number;
  recovery: number;
  consistency: number;
}): string[] {
  const unlocked: string[] = [];
  const threshold = 500;

  if (pillarIntel.strength >= threshold) unlocked.push('strength-pillar');
  if (pillarIntel.endurance >= threshold) unlocked.push('endurance-pillar');
  if (pillarIntel.bodyweight >= threshold) unlocked.push('bodyweight-pillar');
  if (pillarIntel.recovery >= threshold) unlocked.push('recovery-pillar');
  if (pillarIntel.consistency >= threshold) unlocked.push('consistency-pillar');

  return unlocked;
}

/**
 * Get the next rank unlock info based on current lifetime Intel
 */
export function getNextRankUnlock(lifetimeIntel: number): {
  avatar: { id: string; name: string; intelNeeded: number; progress: number } | null;
  title: { id: string; name: string; intelNeeded: number; progress: number } | null;
} {
  // Find next avatar unlock
  let nextAvatar: { id: string; name: string; intelNeeded: number; progress: number } | null = null;
  for (const [id, avatar] of Object.entries(AVATARS)) {
    if (
      avatar.category === 'rank' &&
      avatar.unlockRequirement?.type === 'intel' &&
      avatar.unlockRequirement.value > lifetimeIntel
    ) {
      if (!nextAvatar || avatar.unlockRequirement.value < nextAvatar.intelNeeded) {
        nextAvatar = {
          id,
          name: avatar.name,
          intelNeeded: avatar.unlockRequirement.value,
          progress: Math.round((lifetimeIntel / avatar.unlockRequirement.value) * 100),
        };
      }
    }
  }

  // Find next title unlock
  let nextTitle: { id: string; name: string; intelNeeded: number; progress: number } | null = null;
  for (const [id, title] of Object.entries(TITLES)) {
    if (
      title.category === 'rank' &&
      title.unlockRequirement?.type === 'intel' &&
      title.unlockRequirement.value > lifetimeIntel
    ) {
      if (!nextTitle || title.unlockRequirement.value < nextTitle.intelNeeded) {
        nextTitle = {
          id,
          name: title.name,
          intelNeeded: title.unlockRequirement.value,
          progress: Math.round((lifetimeIntel / title.unlockRequirement.value) * 100),
        };
      }
    }
  }

  return { avatar: nextAvatar, title: nextTitle };
}
